import TurndownService from 'turndown';
import { isEmpty } from 'lodash';

const turndownService = new TurndownService({
  emDelimiter: '',
  strongDelimiter: '',
});
turndownService.escape = x => x; // remove escaping since this text would not be run as html, but only to be shown as is
turndownService.addRule('blocks', { // remove the redundant \n before ol, ul and div elements
  filter: ['ol', 'ul', 'div'],
  replacement: content => `${content}\n`,
});
turndownService.addRule('lineItems', {
  // Taken from the Turndown source and modified to remove new lines from li elements
  // https://github.com/mixmark-io/turndown/blob/master/src/commonmark-rules.js
  filter: 'li',
  replacement: (content, node, options) => {
    // eslint-disable-next-line no-param-reassign
    content = content
      .replace(/^\n+/, '') // remove leading newlines
      .replace(/\n+$/, '\n') // replace trailing newlines with just a single one
      .replace(/\n/gm, '\n    ') // indent
      .replace(/^\s*\n+\s+$/gm, '\n'); // replace the line that begins with spaces, followed by a new line that ends with spaces to a single new line only
    let prefix = `${options.bulletListMarker}   `;
    const parent = node.parentNode;
    if (parent.nodeName === 'OL') {
      const start = parent.getAttribute('start');
      const index = Array.prototype.indexOf.call(parent.children, node);
      prefix = `${start ? Number(start) + index : index + 1}.  `;
    }
    return (
      prefix + content + (node.nextSibling && !/\n$/.test(content) ? '\n' : '')
    );
  },
});
turndownService.addRule('lineBreaks', { // converts <br> html tags to \n
  filter: ['BR'],
  replacement: () => '\n',
});

export const getMessagePlainText = message => turndownService.turndown(message || '');

export function getFirstNode(el) {
  return el.children.length > 0 ? getFirstNode(el.children[0]) : el;
}

export function getFirstNodeText(htmlText) {
  const el = document.createElement('div');
  el.innerHTML = htmlText || '';
  return getFirstNode(el).innerText || '';
}

export const replaceSpecificElement = (innerText, sourceElementType, targetElementType) => {
  /*
    the reverse if because this is a DFS run
    and we dont want to change the hierarchy for the replaced elements
    so we run from tails to head
    */
  const template = document.createElement('template');
  template.innerHTML = innerText;
  const root = template.content || template;
  [...root.querySelectorAll(sourceElementType)].reverse().forEach((srcElementEl) => {
    const parent = srcElementEl.parentElement || root;
    const trgElementEl = document.createElement(targetElementType);
    trgElementEl.innerHTML = srcElementEl.innerHTML;
    parent.replaceChild(trgElementEl, srcElementEl);
  });
  return template.innerHTML.toString().trim();
};

export const removeEmptyHTMLTags = (innerText) => {
  /* the reverse if because this is a DFS run
    and we want to remove elements even if they contain other empty elements inside
    so we run from tail to head */
  const template = document.createElement('template');
  template.innerHTML = innerText;
  const lineBreakElement = 'BR';
  const root = template.content || template;
  [...root.querySelectorAll('*')].reverse().forEach((el) => {
    if (el.tagName !== lineBreakElement && isEmpty(el.childNodes)) { // refactored due to ie11
      const parent = el.parentElement || root;
      parent.removeChild(el);
    }
  });
  return template.innerHTML.toString().trim();
};

const SUPPORTED_HTML_ELEMENTS = [
  'strong', 'i', 'em', 'b', 'u', 'a', 'br', 'span', 'div', 'ul', 'ol', 'li', 'p',
];

export const removeUnsupportedElements = (innerText) => {
  /*
    the reverse if because this is a DFS run
    and we dont want to change the hierarchy for the replaced elements
    so we run from tails to head
    */
  const template = document.createElement('template');
  template.innerHTML = innerText;
  const root = template.content || template;
  [...root.querySelectorAll('*')].reverse().forEach((srcElementEl) => {
    if (!SUPPORTED_HTML_ELEMENTS.includes(srcElementEl.tagName.toLowerCase())) {
      const parent = srcElementEl.parentElement || root;
      const trgElementEl = document.createElement('span');
      trgElementEl.innerHTML = srcElementEl.innerHTML;
      parent.replaceChild(trgElementEl, srcElementEl);
    }
  });
  return template.innerHTML.toString().trim();
};

export default { replaceSpecificElement };
